import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  message
} from 'antd';
import {
  MessageOutlined,
  PoweroffOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { broadcastAPI, schoolAdminAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import useSchoolAdminData from './useSchoolAdminData';
import SchoolWorkbenchView from './SchoolWorkbenchView';

const formatBytes = (value) => {
  const bytes = Number(value || 0);
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const onlineTag = (lastSeenAt) => {
  if (!lastSeenAt) return <Tag>未上报</Tag>;
  const delta = Date.now() - new Date(lastSeenAt).getTime();
  if (delta <= 5 * 60 * 1000) return <Tag color="green">5分钟在线</Tag>;
  if (delta <= 30 * 60 * 1000) return <Tag color="blue">30分钟内</Tag>;
  return <Tag color="default">离线</Tag>;
};

const SchoolAdminConsole = () => {
  const { user, isSuperAdmin, isSchoolAdmin } = useAuth();
  const navigate = useNavigate();
  const [schools, setSchools] = useState([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState(user?.schoolId ? String(user.schoolId) : undefined);
  const [actionModal, setActionModal] = useState(null);
  const [actionForm] = Form.useForm();
  const [teacherImportVisible, setTeacherImportVisible] = useState(false);
  const [teacherImportResult, setTeacherImportResult] = useState(null);
  const [teacherImportForm] = Form.useForm();
  const [qrcodeVisible, setQrcodeVisible] = useState(false);
  const [qrcodeResult, setQrcodeResult] = useState(null);
  const [qrcodeForm] = Form.useForm();

  const schoolId = selectedSchoolId;
  const { loading, overview, workbench, grades, teachers, devices, failedModules, loadData, isCurrentSchool } = useSchoolAdminData(schoolId);
  const schoolOptions = useMemo(() => schools.map((school) => ({
    label: school.name,
    value: String(school.id)
  })), [schools]);

  useEffect(() => {
    let active = true;
    schoolAdminAPI.getSchools().then((response) => {
      if (!active) return;
      if (!response.data.success) throw new Error('School list unavailable');
      const rows = response.data.data || [];
      setSchools(rows);
      setSelectedSchoolId((current) => rows.some((row) => String(row.id) === current) ? current : (rows[0]?.id ? String(rows[0].id) : undefined));
    }).catch(() => { if (active) message.error('加载学校列表失败'); });
    return () => { active = false; };
  }, []);

  const selectSchool = (value) => {
    setActionModal(null);
    setTeacherImportVisible(false);
    setTeacherImportResult(null);
    setQrcodeVisible(false);
    setQrcodeResult(null);
    setSelectedSchoolId(value);
  };

  const openAction = (type, grade) => {
    if (!isCurrentSchool(schoolId) || loading) return;
    setActionModal({ type, grade, schoolId });
    actionForm.resetFields();
    actionForm.setFieldsValue({
      title: type === 'broadcast' ? `${grade}年级通知` : undefined,
      content: ''
    });
  };

  const submitAction = async () => {
    const targetSchoolId = actionModal?.schoolId;
    const values = await actionForm.validateFields();
    const grade = actionModal?.grade;
    if (!isCurrentSchool(targetSchoolId) || !grade || loading) return;
    try {
      if (actionModal.type === 'broadcast') {
        await broadcastAPI.sendMessage({
          schoolId: String(targetSchoolId),
          targetType: 'grade',
          targetId: String(targetSchoolId),
          messageType: 'text',
          displayMode: 'popup',
          title: values.title,
          content: values.content,
          metadata: { grade }
        });
      } else {
        await schoolAdminAPI.sendDeviceBatchCommand({
          schoolId: String(targetSchoolId),
          grade,
          command: actionModal.type,
          payload: { reason: values.content || 'school-admin-console' }
        });
      }
      if (!isCurrentSchool(targetSchoolId)) return;
      message.success('操作已提交');
      setActionModal(null);
      loadData(targetSchoolId);
    } catch (error) {
      console.error(error);
      if (isCurrentSchool(targetSchoolId)) message.error(error.response?.data?.message || '操作失败');
    }
  };

  const removeTeacher = async (accountId) => {
    if (!isCurrentSchool(schoolId) || loading) return;
    const targetSchoolId = schoolId;
    try {
      await schoolAdminAPI.removeTeacher(accountId, { schoolId: targetSchoolId });
      if (!isCurrentSchool(targetSchoolId)) return;
      message.success('已移除教师');
      loadData(targetSchoolId);
    } catch (error) {
      if (isCurrentSchool(targetSchoolId)) message.error(error.response?.data?.message || '移除失败');
    }
  };

  const openTeacherImport = () => {
    if (!isCurrentSchool(schoolId) || loading) return;
    setTeacherImportResult(null);
    teacherImportForm.resetFields();
    teacherImportForm.setFieldsValue({
      text: '',
      maxUses: 1,
      expiryDays: 365
    });
    setTeacherImportVisible(true);
  };

  const submitTeacherImport = async () => {
    if (!schoolId || loading) return;
    const targetSchoolId = schoolId;
    const values = await teacherImportForm.validateFields();
    if (!isCurrentSchool(targetSchoolId)) return;
    try {
      const response = await schoolAdminAPI.importTeacherInvitations({
        schoolId: targetSchoolId,
        text: values.text,
        maxUses: values.maxUses,
        expiryDays: values.expiryDays
      });
      if (!isCurrentSchool(targetSchoolId)) return;
      const data = response.data.data || response.data || {};
      setTeacherImportResult(data);
      message.success(`已生成 ${data.importedCount || 0} 个教师邀请码`);
      loadData(targetSchoolId);
    } catch (error) {
      if (isCurrentSchool(targetSchoolId)) message.error(error.response?.data?.message || '生成教师邀请码失败');
    }
  };

  const openQrcodeGenerator = () => {
    if (!isCurrentSchool(schoolId) || loading) return;
    setQrcodeResult(null);
    qrcodeForm.resetFields();
    qrcodeForm.setFieldsValue({
      joinType: 'parent',
      maxUses: 100,
      expiryDays: 365,
      recreate: false
    });
    setQrcodeVisible(true);
  };

  const submitQrcodeGenerator = async () => {
    if (!schoolId || loading) return;
    const targetSchoolId = schoolId;
    const values = await qrcodeForm.validateFields();
    if (!isCurrentSchool(targetSchoolId)) return;
    try {
      const response = await schoolAdminAPI.generateSchoolClassQrcodes({
        schoolId: targetSchoolId,
        ...values
      });
      if (!isCurrentSchool(targetSchoolId)) return;
      const data = response.data.data || response.data || {};
      setQrcodeResult(data);
      message.success(`已处理 ${data.totalClasses || 0} 个班级二维码`);
      loadData(targetSchoolId);
    } catch (error) {
      if (isCurrentSchool(targetSchoolId)) message.error(error.response?.data?.message || '生成全校二维码失败');
    }
  };

  const gradeColumns = [
    { title: '年级', dataIndex: 'grade', key: 'grade' },
    { title: '班级数', dataIndex: 'classes', key: 'classes' },
    { title: '启用班级', dataIndex: 'activeClasses', key: 'activeClasses' },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<MessageOutlined />} onClick={() => openAction('broadcast', record.grade)}>广播</Button>
          <Button size="small" icon={<ReloadOutlined />} onClick={() => openAction('lock_app', record.grade)}>锁定客户端</Button>
          <Button size="small" danger icon={<PoweroffOutlined />} onClick={() => openAction('shutdown_system', record.grade)}>关机</Button>
        </Space>
      )
    }
  ];

  const teacherColumns = [
    { title: '姓名', dataIndex: 'displayName', key: 'displayName' },
    { title: '账号', dataIndex: 'username', key: 'username' },
    { title: '角色', dataIndex: 'role', key: 'role', render: (role) => <Tag color={role === 'school_admin' ? 'blue' : 'green'}>{role === 'school_admin' ? '学校管理员' : '教师'}</Tag> },
    { title: '班级数', dataIndex: 'classCount', key: 'classCount' },
    { title: '电话', dataIndex: 'phone', key: 'phone', render: (value) => value || '-' },
    { title: '邮箱', dataIndex: 'email', key: 'email', render: (value) => value || '-' },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => record.role === 'teacher' ? (
        <Popconfirm title="确认将该教师移出本校？" onConfirm={() => removeTeacher(record.id)}>
          <Button danger size="small">移除</Button>
        </Popconfirm>
      ) : '-'
    }
  ];

  const deviceColumns = [
    { title: '设备', dataIndex: 'deviceName', key: 'deviceName', render: (value, row) => value || row.deviceUid },
    { title: '班级', dataIndex: 'className', key: 'className', render: (value) => value || '-' },
    { title: '年级', dataIndex: 'grade', key: 'grade', render: (value) => value || '-' },
    { title: '系统', key: 'os', render: (_, row) => [row.osPlatform, row.osName, row.osDistro].filter(Boolean).join(' / ') || '-' },
    { title: '版本', dataIndex: 'clientVersion', key: 'clientVersion', render: (value) => value || '-' },
    { title: '状态', dataIndex: 'lastSeenAt', key: 'lastSeenAt', render: onlineTag }
  ];

  const today = overview?.today || {};
  const totals = overview?.totals || {};
  const coverage = workbench?.coverage || {};
  const school = overview?.school || {};
  const schoolRegion = [school.province, school.city, school.county].filter(Boolean).join(' ');
  const profileMissing = [
    !schoolRegion ? '学校地区' : null,
    !school.address ? '详细地址' : null,
    !school.phone ? '联系电话' : null
  ].filter(Boolean);
  const onboardingChecklist = [
    { key: 'profile', title: '学校资料', done: profileMissing.length === 0, desc: profileMissing.length === 0 ? '基础资料已完善' : `可补充：${profileMissing.join('、')}` },
    { key: 'classes', title: '班级数据', done: Number(totals.classes || 0) > 0, desc: `${totals.classes || 0} 个班级` },
    { key: 'teachers', title: '教师账号', done: Number(totals.teachers || 0) > 0, desc: `${totals.teachers || 0} 名教师` },
    { key: 'students', title: '学生资料', done: Number(totals.students || 0) > 0, desc: `${totals.students || 0} 名学生` },
    { key: 'parents', title: '家长绑定', done: Number(coverage.parentBindingRate || 0) > 0, desc: `绑定率 ${coverage.parentBindingRate || 0}%` },
    { key: 'devices', title: '桌面客户端', done: Number(totals.devices || 0) > 0, desc: `${totals.devices || 0} 台设备` },
    { key: 'schedules', title: '课程表', done: Number(coverage.classesWithSchedule || 0) > 0, desc: `${coverage.classesWithSchedule || 0} 个班级已配置` },
    { key: 'qrcodes', title: '加入二维码', done: Number(today.invitations || 0) > 0, desc: `今日生成 ${today.invitations || 0} 个` }
  ];
  const readyCount = onboardingChecklist.filter((item) => item.done).length;
  const readyPercent = Math.round((readyCount / onboardingChecklist.length) * 100);

  return (
    <div>
      <SchoolWorkbenchView
        schools={schools} schoolId={schoolId} schoolOptions={schoolOptions} selectSchool={selectSchool}
        platformManager={isSuperAdmin()} showNotifications={isSchoolAdmin()}
        loading={loading} overview={overview} workbench={workbench} grades={grades} teachers={teachers} devices={devices}
        failedModules={failedModules} refresh={() => loadData(schoolId)} navigate={navigate}
        profileMissing={profileMissing} schoolRegion={schoolRegion} onboardingChecklist={onboardingChecklist} readyPercent={readyPercent}
        gradeColumns={gradeColumns} teacherColumns={teacherColumns} deviceColumns={deviceColumns}
        openTeacherImport={openTeacherImport} openQrcodeGenerator={openQrcodeGenerator} formatBytes={formatBytes}
      />

      <Modal
        open={teacherImportVisible}
        title="批量生成教师邀请码"
        onCancel={() => setTeacherImportVisible(false)}
        onOk={submitTeacherImport}
        okText="生成邀请码"
        width={860}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="每行一个教师：教师姓名,班级1,班级2"
          description="教师微信登录时输入生成的邀请码，会自动加入学校，并获得该行填写班级的教师权限。"
        />
        <Form form={teacherImportForm} layout="vertical">
          <Form.Item
            name="text"
            label="教师与班级"
            rules={[{ required: true, message: '请粘贴教师和班级信息' }]}
          >
            <Input.TextArea
              rows={8}
              placeholder={'张老师,701班,702班\n李老师,703班\n王老师,一年级一班,一年级二班'}
            />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="maxUses" label="每个邀请码可使用次数">
                <Input type="number" min={1} max={100} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="expiryDays" label="有效天数">
                <Input type="number" min={1} max={3650} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        {teacherImportResult && (
          <Table
            size="small"
            rowKey={(row) => row.id || row.teacherName}
            pagination={{ pageSize: 6 }}
            dataSource={teacherImportResult.imported || []}
            columns={[
              { title: '教师', dataIndex: 'teacherName', key: 'teacherName' },
              { title: '邀请码', dataIndex: 'code', key: 'code' },
              { title: '授权班级', dataIndex: 'classNames', key: 'classNames', render: (value) => (value || []).join('、') || '全校教师身份' },
              { title: '未匹配班级', dataIndex: 'missingClassNames', key: 'missingClassNames', render: (value) => (value || []).join('、') || '-' }
            ]}
          />
        )}
      </Modal>

      <Modal
        open={qrcodeVisible}
        title="一键生成全校班级二维码"
        onCancel={() => setQrcodeVisible(false)}
        onOk={submitQrcodeGenerator}
        okText="生成二维码代码"
        width={860}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="默认复用已有有效二维码"
          description="如需重新生成，可选择重新生成。二维码图片仍由现有班级二维码页面根据代码展示。"
        />
        <Form form={qrcodeForm} layout="vertical">
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="joinType" label="二维码类型">
                <Select options={[
                  { label: '家长加入', value: 'parent' },
                  { label: '教师加入', value: 'teacher' }
                ]} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="maxUses" label="每班可使用次数">
                <Input type="number" min={1} max={5000} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="expiryDays" label="有效天数">
                <Input type="number" min={1} max={3650} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="recreate" label="处理方式">
            <Select options={[
              { label: '复用已有有效代码', value: false },
              { label: '重新生成新代码', value: true }
            ]} />
          </Form.Item>
        </Form>
        {qrcodeResult && (
          <Table
            size="small"
            rowKey={(row) => row.id || `${row.classId}-${row.code}`}
            pagination={{ pageSize: 8 }}
            dataSource={qrcodeResult.codes || []}
            columns={[
              { title: '年级', dataIndex: 'grade', key: 'grade', render: (value) => value || '-' },
              { title: '班级', dataIndex: 'className', key: 'className' },
              { title: '代码', dataIndex: 'code', key: 'code' },
              { title: '状态', dataIndex: 'reused', key: 'reused', render: (value) => value ? <Tag>复用</Tag> : <Tag color="green">新建</Tag> }
            ]}
          />
        )}
      </Modal>

      <Modal
        open={!!actionModal}
        title={actionModal?.type === 'broadcast' ? `发送年级广播：${actionModal?.grade}` : `年级设备命令：${actionModal?.grade}`}
        onCancel={() => setActionModal(null)}
        onOk={submitAction}
        okText="提交"
      >
        <Form form={actionForm} layout="vertical">
          {actionModal?.type === 'broadcast' && (
            <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
              <Input maxLength={80} />
            </Form.Item>
          )}
          <Form.Item name="content" label={actionModal?.type === 'broadcast' ? '内容' : '原因/备注'} rules={[{ required: actionModal?.type === 'broadcast', message: '请输入内容' }]}>
            <Input.TextArea rows={4} maxLength={1000} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SchoolAdminConsole;
